<template>
	<div class="alert flex items-center" role="alert" :style="{backgroundColor: props.color}"
		 :class="[props.type ? 'alert-' + props.type : '', shadowableString ? `shadow-${shadowableString}` : undefined, props.spaced ? 'py-24 px-16' : (props.small ? 'px-8 py-4' : 'p-16'), {'mb-8 sm:mb-16': !props.noSpacing}, responsiveGridBorderRadiusClass, {'alert-text': props.text, 'text-white': !!props.color}]">
		<div class="flex justify-center w-full">
			<div v-if="displayIcon" class="p-8 relative mr-24" :class="{'none sm:block': props.hideIconOnMobile}">
				<Icon :icon="displayIcon" :x-small="props.small" prominent/>
			</div>
			<div class="alert__content flex flex-column justify-center flex-grow-x-1" :class="{'sm:flex-row sm:items-center sm:justify-between': !props.vertical}">
				<div v-if="props.content" v-html="props.content" class="flex-grow-x-1" :class="{'text-xs': props.small}"/>
				<slot/>

				<div v-if="props.button || $slots.button" class="mt-16 w-full" :class="{'sm:mt-0 sm:ml-16 sm:w-auto': !props.vertical}">
					<wrapper :grow="props.vertical">
						<slot name="button">
							<btn @click="emit('click')" :to="props.to" :color="props.type ? `${props.type} lighten` : 'success'" :loading="props.loading" class="min-w-20">
								<Icon v-if="props.buttonIcon" :icon="props.buttonIcon"/>
								<Icon v-else-if="props.button && props.buttonIcon !== false" :icon="icons.create"/>
								<span>{{ props.button === true ? $t('action.add') : props.button }}</span>
							</btn>
						</slot>
					</wrapper>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {cancelIcon, checkIcon, createIcon, infoIcon, warningIcon} from "../../config";
import {type GridDisplayableProps, useGridDisplayable} from "../../composables/useGridDisplayable";
import {isString} from "@spoferan/spoferan-ts-core";
import {type ShadowableProps, useShadowable} from "../../composables/useShadowable";
import {computed} from 'vue';

const props = withDefaults(defineProps<{
	// Renders the alert without a lighter background
	text?: boolean,

	// The HTML content to render in the alert
	content?: string,

	// The text to display on a button to navigate the user to a corresponding page
	// or true to display the button with a default 'add' text
	button?: string | boolean,

	// The icon to place on the button
	buttonIcon?: string | boolean,

	// The route to navigate the user to when he clicks on the button
	to?: string | object,

	type?: 'success' | 'warning' | 'error' | 'info' | 'primary',

	icon?: string | boolean,

	hideIconOnMobile?: boolean,

	// Whether to vertically align the content even on desktop devices
	vertical?: boolean,

	// Whether a larger spacing shall be applied
	spaced?: boolean,

	// Whether a small height shall be applied
	small?: boolean,

	// The hex color to use for the background color
	color?: string,

	// Whether the alert shall receive a loading state
	loading?: boolean,
} & GridDisplayableProps & ShadowableProps>(), {
	icon: true,
	type: 'info',
	rounded: 'lg'
});

const {responsiveGridBorderRadiusClass} = useGridDisplayable(props);
const {shadowableString} = useShadowable(props);

const emit = defineEmits(['click']);

const icons = {
	create: createIcon,
	check: checkIcon,
	warning: warningIcon,
	cancel: cancelIcon,
	info: infoIcon,
};

const displayIcon = computed((): string|null => {
	// noinspection PointlessBooleanExpressionJS - null shall be treated as true
	if (props.icon === false) {
		return null;
	}
	if (isString(props.icon)) {
		return props.icon as string;
	}
	switch (props.type) {
		case 'success':
			return icons.check;
		case 'warning':
			return icons.warning;
		case 'error':
			return icons.cancel;
	}

	return icons.info;
});
</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.app .alert {
  font-size: 14px;
  position: relative;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  color: #5e6b6b;
  background-color: #FBFBFB;
  width: 100%;
  box-sizing: border-box;
}
.app .alert .alert__content, .app .alert .alert__content p {
  line-height: 1.3;
}
.app .alert .alert__content > div > p:last-child {
  margin-bottom: 0;
}
.app .alert.alert-text:before {
  background-color: currentColor;
  border-radius: inherit;
  bottom: 0;
  content: "";
  left: 0;
  opacity: 0.12;
  position: absolute;
  pointer-events: none;
  right: 0;
  top: 0;
}
.app .alert.alert-white {
  color: #ffffff;
  background-color: #ffffff;
}
.app .alert.alert-white:focus, .app .alert.alert-white.alert-active {
  background-color: white;
}
.app .alert.alert-white.alert-text {
  background-color: transparent;
  color: #ffffff;
}
.app .alert.alert-black {
  color: #ffffff;
  background-color: #2e3535;
}
.app .alert.alert-black:focus, .app .alert.alert-black.alert-active {
  background-color: #333a3a;
}
.app .alert.alert-black.alert-text {
  background-color: transparent;
  color: #2e3535;
}
.app .alert.alert-primary {
  color: #ffffff;
  background-color: #008200;
}
.app .alert.alert-primary:focus, .app .alert.alert-primary.alert-active {
  background-color: #008c00;
}
.app .alert.alert-primary.alert-text {
  background-color: transparent;
  color: #008200;
}
.app .alert.alert-secondary {
  color: #ffffff;
  background-color: #f0820a;
}
.app .alert.alert-secondary:focus, .app .alert.alert-secondary.alert-active {
  background-color: #f5870f;
}
.app .alert.alert-secondary.alert-text {
  background-color: transparent;
  color: #f0820a;
}
.app .alert.alert-error {
  color: #ffffff;
  background-color: #FA5555;
}
.app .alert.alert-error:focus, .app .alert.alert-error.alert-active {
  background-color: #fa5f5f;
}
.app .alert.alert-error.alert-text {
  background-color: transparent;
  color: #FA5555;
}
.app .alert.alert-success {
  color: #ffffff;
  background-color: #47a447;
}
.app .alert.alert-success:focus, .app .alert.alert-success.alert-active {
  background-color: #4aab4a;
}
.app .alert.alert-success.alert-text {
  background-color: transparent;
  color: #47a447;
}
.app .alert.alert-warning {
  color: #ffffff;
  background-color: #EB9E05;
}
.app .alert.alert-warning:focus, .app .alert.alert-warning.alert-active {
  background-color: #f5a505;
}
.app .alert.alert-warning.alert-text {
  background-color: transparent;
  color: #EB9E05;
}
.app .alert.alert-transparent {
  color: #ffffff;
  background-color: transparent;
}
.app .alert.alert-transparent:focus, .app .alert.alert-transparent.alert-active {
  background-color: rgba(5, 5, 5, 0);
}
.app .alert.alert-transparent.alert-text {
  background-color: transparent;
  color: transparent;
}
</style>